import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Logo } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGIN, REGISTER } from "@/constants/testIds";

const Shell = ({ title, sub, children }) => (
  <main className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
    <div className="hidden lg:block relative bg-stone-900 overflow-hidden grain">
      <img src="https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=1400&q=80" alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950 to-transparent" />
      <div className="absolute bottom-12 left-12 right-12 text-white">
        <Logo className="h-12 w-12" />
        <p className="mt-6 font-display text-5xl font-black leading-[1.05]">Your app account<br />works here too.</p>
      </div>
    </div>
    <div className="flex items-center justify-center px-4 py-14 pb-28">
      <div className="w-full max-w-sm rise">
        <h1 className="font-display text-4xl font-black tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-stone-500">{sub}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </main>
);

const Field = ({ id, label, testId, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={testId} className="h-11 rounded-xl bg-white" {...p} /></div>
);

const Submit = ({ busy, testId, children }) => (
  <button data-testid={testId} disabled={busy} className="w-full h-12 rounded-full bg-[#FF5C00] hover:bg-[#E04E00] disabled:opacity-60 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
  </button>
);

const useAuthSubmit = (fn) => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await fn(); nav(sp.get("next") || "/events"); } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return { busy, submit, next: sp.get("next") };
};

export const LoginPage = () => {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const { busy, submit, next } = useAuthSubmit(() => login(f.email.trim(), f.password));
  return (
    <Shell title="Welcome back" sub="Log in with the same email and password you use in the app.">
      <form onSubmit={submit} className="space-y-4">
        <Field id="email" label="Email" type="email" required testId={LOGIN.emailInput} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="password" label="Password" type="password" required testId={LOGIN.passwordInput} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Submit busy={busy} testId={LOGIN.submitButton}>Log in</Submit>
      </form>
      <p className="mt-6 text-sm text-stone-500">New here? <Link to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`} data-testid={LOGIN.registerLink} className="font-semibold text-stone-900 hover:text-[#FF5C00]">Create an account</Link></p>
    </Shell>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", city: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const { busy, submit, next } = useAuthSubmit(() => register({
    ...f, email: f.email.trim(), city: f.city || null,
    referred_by_id: sp.get("ref") || null, invite_token: sp.get("invite") || null,
  }));
  return (
    <Shell title="Join Creator Series" sub="One account for the website and the mobile app.">
      <form onSubmit={submit} className="space-y-4">
        <Field id="name" label="Full name" required testId={REGISTER.nameInput} value={f.name} onChange={set("name")} />
        <Field id="email" label="Email" type="email" required testId={REGISTER.emailInput} value={f.email} onChange={set("email")} />
        <Field id="password" label="Password" type="password" required minLength={6} testId={REGISTER.passwordInput} value={f.password} onChange={set("password")} />
        <Field id="city" label="City (optional)" testId="register-city-input" value={f.city} onChange={set("city")} />
        <Submit busy={busy} testId={REGISTER.submitButton}>Create account</Submit>
      </form>
      <p className="mt-6 text-sm text-stone-500">Already a member? <Link to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`} data-testid={REGISTER.loginLink} className="font-semibold text-stone-900 hover:text-[#FF5C00]">Log in</Link></p>
    </Shell>
  );
};
